import React, { useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import UsernameEditor from "@clockwise/web-commons/src/ui/scheduling-links/UsernameEditor";
import LinkAdminCard from "@clockwise/web-commons/src/ui/scheduling-links/LinkAdminCard";
import CreateLinkCard from "@clockwise/web-commons/src/ui/scheduling-links/CreateLinkCard";
import {
  SCHEDULING_LINKS_QUERY,
  UPDATE_USER_SHORTNAME_MUTATION,
} from "@clockwise/web-commons/src/ui/scheduling-links/queries.graphql";
import { UPDATE_LINK_ACTIVE } from "@clockwise/web-commons/src/mutations/UpdateLinkActive.graphql";
import linkSrc from "./images/link.png";
import { compact } from "lodash";
import { useRouter } from "found";
import { paths } from "@clockwise/client-commons/src/constants/site";
import { Button } from "@material-ui/core";
import { AddCircleOutline } from "@material-ui/icons";
import { getBulkExperiments } from "@util/experiment.util";
import { getCurrentOrg } from "@util/org.util";
import { useUsernameValidator } from "@clockwise/web-commons/src/ui/scheduling-links/hooks/useUsernameValidator";

import {
  SchedulingLinksQuery,
  SchedulingLinksQueryVariables,
} from "@clockwise/web-commons/src/ui/scheduling-links/__generated__/SchedulingLinksQuery";
import {
  UpdateLinkActive,
  UpdateLinkActiveVariables,
} from "@clockwise/web-commons/src/mutations/__generated__/UpdateLinkActive";
import {
  UpdateUserShortname,
  UpdateUserShortnameVariables,
} from "@clockwise/web-commons/src/ui/scheduling-links/__generated__/UpdateUserShortname";
import { logger } from "@src/util/logger.util";
import { RouteRenderer } from "@components/route-renderer";
import { getValue } from "@clockwise/web-commons/src/util/errorable.util";
import { LinksDashboardLoadingCard } from "@clockwise/web-commons/src/ui/links-dashboard-loading-card";

// tracking
import { TrackingEvents, useTrackOnce } from "@clockwise/web-commons/src/util/analytics.util";

export default function SchedulingLinkDashboard() {
  const { router } = useRouter();
  const trackViewDashboard = useTrackOnce(TrackingEvents.LINKS.DASHBOARD.VIEW);
  /*
     Mutations
  */
  const [updateLinkActive, { error: updateLinkError }] = useMutation<
    UpdateLinkActive,
    UpdateLinkActiveVariables
  >(UPDATE_LINK_ACTIVE);

  const [updateUserShortname, { error: updateShortnameError }] = useMutation<
    UpdateUserShortname,
    UpdateUserShortnameVariables
  >(UPDATE_USER_SHORTNAME_MUTATION, {
    // FIXME (lsanwick) This only appears to be necessary because Apollo cache is not propagating
    // correctly after update, likely due to lack of ids on objects.
    awaitRefetchQueries: true,
    refetchQueries: [SCHEDULING_LINKS_QUERY],
  });

  /*
     Queries
  */
  const { data, loading, error } = useQuery<SchedulingLinksQuery, SchedulingLinksQueryVariables>(
    SCHEDULING_LINKS_QUERY,
    {
      variables: {
        createLinkNameIfNone: true,
      },
    },
  );

  if (error) {
    logger.error(error.message);
  }

  const viewer = data?.viewer || null;
  const user = viewer?.user || null;
  const org = getCurrentOrg(viewer);
  const linkName =
    user?.linkNameErrorable?.__typename === "LinkName" ? user.linkNameErrorable.linkName : null;
  const schedulingLinks = compact(viewer?.user?.schedulingLinks || []);
  const experiments = getBulkExperiments(org);

  const value = linkName || "";

  const { validate, error: usernameError, validating } = useUsernameValidator(value);

  useEffect(() => {
    if (schedulingLinks && !loading) {
      // If making changes here, ensure tracking event fires just once.
      trackViewDashboard({
        linkCount: schedulingLinks.length,
      });
    }
  }, [schedulingLinks, loading, trackViewDashboard]);

  const onChangeActive = (slug: string) => async (isActive: boolean) => {
    try {
      const { data } = await updateLinkActive({ variables: { slug, isActive } });
      if (!data) {
        logger.error("There was an error updating the active status of the link", updateLinkError);
      }
    } catch (error) {
      logger.error("There was an error updating the active status of the link", error);
    }
  };
  const onCreateLink = () => router.push(paths.createSchedulingLink);
  const onEditLink = (slug: string) => router.push(paths.editSchedulingLink.replace(":slug", slug));
  const onClickLinkCard = (linkUrl: string) => router.push(linkUrl);

  const onSaveUsername = async (shortname: string) => {
    const { data } = await updateUserShortname({ variables: { shortname } });

    const user = getValue(data?.updateUserShortname?.userErrorable);
    const linkName = getValue(user?.linkNameErrorable)?.linkName;

    if (updateShortnameError || linkName !== shortname) {
      logger.error("There was an error updating the user's shortname", updateShortnameError);
    }
  };

  return (
    <RouteRenderer
      notFound={!loading && !experiments.LinkScheduler}
      loading={false}
      error={!!error}
    >
      <div className="cw-font-brand cw-max-w-screen-lg cw-grow cw-px-5 md:cw-px-12">
        <div className="cw-mx-auto cw-px-2 sm:cw-px-4 cw-pt-4 sm:cw-pt-12 cw-pb-4 cw-flex cw-flex-col cw-gap-6 cw-h-screen">
          <div className="cw-flex cw-justify-between">
            <div>
              <div className="cw-flex cw-items-center cw-gap-3 cw-mb-2 sm:cw-mb-0">
                <h1 className="cw-font-bold cw-text-4xl cw-text-dusk-standard">Scheduling Links</h1>
              </div>
              <UsernameEditor
                username={linkName ?? ""}
                loading={loading}
                onSaveUsername={onSaveUsername}
                onValidateUsername={validate}
                validating={validating}
                error={usernameError}
              />
            </div>
            <div className="cw-hidden sm:cw-flex cw-items-center cw-justify-center cw-rounded-lg cw-h-[76px] cw-w-[76px] cw-p-4 cw-bg-green-700 cw-bg-opacity-20 cw-ml-4">
              <img className="cw-h-11 cw-w-11" src={linkSrc} alt="Chain link icon" />
            </div>
          </div>
          <div className="cw-flex cw-flex-col cw-gap-2">
            <h1 className="cw-font-bold cw-text-2xl cw-text-dusk-standard">My links</h1>
            <div
              className="cw-grid cw-gap-8 cw-mb-8"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
            >
              {loading && <LinksDashboardLoadingCard />}
              {schedulingLinks.map((link) => (
                <LinkAdminCard
                  key={link.meetingSettings.slug}
                  link={link}
                  onChangeActive={onChangeActive(link.meetingSettings.slug)}
                  onClickCard={onClickLinkCard}
                  onEditLink={onEditLink}
                />
              ))}
              <div className="cw-hidden sm:cw-block">
                <CreateLinkCard onClick={onCreateLink} />
              </div>
            </div>
          </div>
          <div className="cw-block sm:cw-hidden cw-sticky cw-bottom-0 cw-left-0 cw-right-0 cw-p-2">
            <Button
              variant="contained"
              color="primary"
              size="large"
              className="cw-w-full cw-rounded-full cw-py-4"
              startIcon={<AddCircleOutline />}
              onClick={onCreateLink}
              cw-id="dashboard-create-link-button"
            >
              <span className="cw-leading-none">CREATE A LINK</span>
            </Button>
          </div>
        </div>
      </div>
    </RouteRenderer>
  );
}
